<template>
  <div class="inline-block">
    <button
      ref="button" type="button" :disabled="disabled"
      class="flex items-center gap-2 px-3 py-1.5 text-sm text-left bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-default min-w-[11rem] max-w-[16rem]"
      @click="toggle"
    >
      <font-awesome-icon :icon="['fas', open ? 'folder-open' : 'folder']" class="text-gray-400 shrink-0" />
      <span class="flex-1 truncate" :class="modelValue ? 'text-gray-900 sensitive' : 'text-gray-500'">{{ modelValue ? modelValue.split('/').join(' / ') : 'Top level' }}</span>
      <font-awesome-icon :icon="['fas', 'chevron-down']" class="text-gray-400 text-[10px] shrink-0" />
    </button>

    <template v-if="open">
      <div class="fixed inset-0 z-40" @click="close"></div>
      <div class="fixed z-50 w-72 max-h-80 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg py-1" :style="position">
        <div
          class="group flex items-center gap-2 px-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50"
          :class="{ 'bg-primary-100': !modelValue }"
          @click="choose('')"
        >
          <font-awesome-icon :icon="['fas', 'folder']" class="text-gray-400" />
          <span class="flex-1 text-gray-900">Top level</span>
          <font-awesome-icon v-if="!modelValue" :icon="['fas', 'check']" class="text-primary-600 text-xs" />
          <button type="button" class="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-gray-900 px-1" title="New folder" @click.stop="startAdd('')">
            <font-awesome-icon :icon="['fas', 'plus']" class="text-xs" />
          </button>
        </div>
        <form v-if="adding === ''" class="flex items-center gap-2 px-3 py-1" @submit.prevent="create">
          <input v-model="draft" type="text" maxlength="60" placeholder="New folder name" class="flex-1 border border-gray-300 rounded px-2 py-1 text-sm" @keydown.esc.stop="adding = null" />
        </form>
        <template v-for="row in rows" :key="row.stem">
          <div
            class="group flex items-center gap-1 pr-3 py-1.5 text-sm cursor-pointer hover:bg-gray-50"
            :class="{ 'bg-primary-100': row.stem === modelValue }"
            :style="{ paddingLeft: `${12 + row.depth * 16}px` }"
            @click="choose(row.stem)"
          >
            <button v-if="row.hasChildren" type="button" class="w-4 text-gray-500 text-[10px]" @click.stop="toggleRow(row.stem)">
              <font-awesome-icon :icon="['fas', expanded[row.stem] ? 'chevron-down' : 'chevron-right']" />
            </button>
            <span v-else class="w-4"></span>
            <font-awesome-icon :icon="['fas', expanded[row.stem] ? 'folder-open' : 'folder']" class="text-gray-400 mr-1" />
            <span class="flex-1 truncate text-gray-900 sensitive">{{ row.name }}</span>
            <font-awesome-icon v-if="row.stem === modelValue" :icon="['fas', 'check']" class="text-primary-600 text-xs" />
            <button type="button" class="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-gray-900 px-1" title="New subfolder" @click.stop="startAdd(row.stem)">
              <font-awesome-icon :icon="['fas', 'plus']" class="text-xs" />
            </button>
          </div>
          <form v-if="adding === row.stem" class="flex items-center gap-2 py-1 pr-3" :style="{ paddingLeft: `${28 + (row.depth + 1) * 16}px` }" @submit.prevent="create">
            <input v-model="draft" type="text" maxlength="60" placeholder="New folder name" class="flex-1 border border-gray-300 rounded px-2 py-1 text-sm" @keydown.esc.stop="adding = null" />
          </form>
        </template>
        <div class="border-t border-gray-100 mt-1 pt-1">
          <button type="button" class="flex items-center gap-2 w-full px-3 py-1.5 text-sm text-left text-gray-700 hover:bg-gray-50" @click="startAdd(modelValue)">
            <font-awesome-icon :icon="['fas', 'plus']" class="text-xs text-gray-500" />
            <span class="truncate">New folder{{ modelValue ? ' in ' : '' }}<span v-if="modelValue" class="sensitive">{{ modelValue.split('/').join(' / ') }}</span></span>
          </button>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { allStems, breadcrumb, stemParts } from '@/utils/pageFolders';

export default {
  name: 'FolderPicker',
  components: { FontAwesomeIcon },
  props: {
    modelValue: { type: String, default: '' },
    stems: { type: Array, default: () => [] },
    disabled: { type: Boolean, default: false },
  },
  emits: ['update:modelValue'],
  data() {
    return { open: false, expanded: {}, adding: null, draft: '', position: {} };
  },
  computed: {
    known() {
      return allStems([...this.stems, this.modelValue].filter(Boolean).map((stem) => ({ stem })));
    },
    rows() {
      const rows = [];
      const walk = (parent, depth) => {
        this.known
          .filter((stem) => stemParts(stem).length === depth + 1 && (!parent || stem.startsWith(`${parent}/`)))
          .forEach((stem) => {
            const hasChildren = this.known.some((other) => other.startsWith(`${stem}/`));
            rows.push({ stem, depth, name: stemParts(stem)[depth], hasChildren });
            if (hasChildren && this.expanded[stem]) walk(stem, depth + 1);
          });
      };
      walk('', 0);
      return rows;
    },
  },
  methods: {
    toggle() {
      if (this.open) {
        this.close();
        return;
      }
      const rect = this.$refs.button.getBoundingClientRect();
      const room = window.innerHeight - rect.bottom;
      this.position = room < 340 && rect.top > room
        ? { left: `${rect.left}px`, bottom: `${window.innerHeight - rect.top + 4}px` }
        : { left: `${rect.left}px`, top: `${rect.bottom + 4}px` };
      this.expanded = Object.fromEntries(breadcrumb(this.modelValue).map((part) => [part.stem, true]));
      this.adding = null;
      this.open = true;
    },
    close() {
      this.open = false;
      this.adding = null;
    },
    toggleRow(stem) {
      this.expanded = { ...this.expanded, [stem]: !this.expanded[stem] };
    },
    choose(stem) {
      this.close();
      if (stem !== this.modelValue) this.$emit('update:modelValue', stem);
    },
    startAdd(parent) {
      this.adding = parent;
      this.draft = '';
      if (parent) this.expanded = { ...this.expanded, [parent]: true };
      this.$nextTick(() => {
        const input = this.$el.querySelector('form input');
        if (input) input.focus();
      });
    },
    create() {
      const name = this.draft.trim().replace(/\//g, '-');
      if (!name) return;
      const parent = this.adding;
      this.choose(parent ? `${parent}/${name}` : name);
    },
  },
};
</script>
